import React, { useState, useEffect } from "react";
import { Wrapper } from "./style";
import { MakeApiCall } from "../../../apis";
import {
    message,
    Table,
    Button,
    Checkbox,
    ConfigProvider,
    Pagination,
    Menu,
    Upload,
    Dropdown,
    Input,
    Alert, // Added Alert
} from "antd";
import { Link } from "react-router-dom";
import { fakeProImage } from "../../../config/fakeData";
import { CheckOutlined, CloseOutlined, DownloadOutlined, EditOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import AddCogs from "./lib/addCogs";

const AMAZON_BASE_URL = "https://www.amazon.in/dp/";

const Products = (props) => {
    const { userData } = props;

    // --- Connection Logic ---
    const isSpApiConnected = userData?.user?.user_data?.is_amz_sp_api_connected === 1;
    const isAdsApiConnected = userData?.user?.user_data?.is_amz_ads_connected === 1;

    const [totalPage, setTotalPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);

    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);
    const [searchText, setSearchText] = useState("");

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const [editingCell, setEditingCell] = useState({ key: null, field: null });
    const [tempValue, setTempValue] = useState("");

    const getProductList = async (data) => {
        if (!isSpApiConnected) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const response = await MakeApiCall(
            `get-products?search_text=${data?.searchText || searchText}&page=${
                data?.page || page || 1
            }&per-page=${data?.pageSize || pageSize}`,
            "get",
            null,
            true,
            {},
            userData?.token
        );

        if (response?.status) {
            setTotalPage(response?.data?.pagination?.totalCount || 0);
            setPage(response?.data?.pagination?.page || 1);
            setList(response?.data?.records || []);
            setLoading(false);
        } else {
            message.warning(response?.message);
            setLoading(false);
            setList([]);
        }
    };

    const updateField = async (record, field, newValue) => {
        if (!isSpApiConnected) return; // Guard for updates

        const finalValue = field === "tags" ? newValue : parseFloat(newValue);
        const payload = { [field]: finalValue };

        const response = await MakeApiCall(
            `update-product/${record.id}`,
            "post",
            payload,
            true,
            null,
            userData?.token
        );

        if (response?.status) {
            message.success("Updated successfully!");
            getProductList();
        } else {
            message.error(response?.message || "Update failed");
        }
    };

    useEffect(() => {
        if (isSpApiConnected) {
            getProductList({ page, pageSize });
        } else {
            setLoading(false);
            setList([]);
        }
    }, [page, pageSize, searchText, isSpApiConnected]);

    const startEditing = (record, field, value) => {
        if (!isSpApiConnected) return;
        setEditingCell({ key: record.id, field });
        setTempValue(value ?? "");
    };

    // ... (rest of helper functions finishEditing & editableCell remain unchanged)
    const finishEditing = async (record, field) => {
        setEditingCell({ key: null, field: null });
        await updateField(record, field, tempValue);
    };

    const editableCell = (record, field, value) => {
        const isEditing = editingCell.key === record.id && editingCell.field === field;
        const isNumberField = field !== "tags";

        if (!isEditing) {
            return (
                <div
                    className="editable-cell-container"
                    onClick={() => startEditing(record, field, value)}
                    style={{
                        cursor: isSpApiConnected ? "pointer" : "default",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        transition: "all 0.2s"
                    }}
                >
                    <EditOutlined style={{ color: "#f45b68", fontSize: "12px" }} />
                    <span className="text-gray-800">
                {value ?? "N/A"}
            </span>
                </div>
            );
        }

        return (
            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <Input
                    autoFocus
                    size="small"
                    type={isNumberField ? "number" : "text"}
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    style={{ width: "120px" }}
                    onPressEnter={() => finishEditing(record, field)}
                />
                <CheckOutlined
                    onClick={() => finishEditing(record, field)}
                    style={{ color: "green", fontSize: 16, cursor: "pointer" }}
                />
                <CloseOutlined
                    onClick={() => {
                        setTempValue(value ?? "");
                        setEditingCell({ key: null, field: null });
                    }}
                    style={{ color: "red", fontSize: 16, cursor: "pointer" }}
                />
            </div>
        );
    };

    const columns = [
        {
            title: "#",
            key: "index",
            fixed: 'left',
            width: 50,
            render: (text, record, index) => (page - 1) * pageSize + index + 1,
        },
        {
            title: "Product Details",
            dataIndex: "productDetails",
            key: "productDetails",
            fixed: 'left',
            width: 300,
            render: (_, record) => (
                <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2">
                    <div className="symbol symbol-65px overflow-hidden me-3">
                        <img src={record?.item_image || fakeProImage} alt="" />
                    </div>
                    <div className="d-flex flex-column">
                        <a
                            className="text-gray-800 fs-6 text-hover-danger mb-1 fw-bolder text-one min-w-200px"
                            href={record?.asin ? `${AMAZON_BASE_URL}${record.asin}` : "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {record?.item_name || ""}
                        </a>
                        <div className="fs-7 mt-1">
                            <span><b>SKU:</b> {record?.sku}</span> / <span><b>ASIN:</b> {record?.asin}</span>
                        </div>
                        <div className="fs-7 mt-1">
                            <span><b>{record?.currency} {record?.item_price}</b></span> / <span> {record?.item_type}</span>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: "Tags",
            dataIndex: "tags",
            key: "tags",
            render: (value, record) => editableCell(record, "tags", value),
        },
        {
            title: "COGS",
            dataIndex: "item_cogs",
            key: "item_cogs",
            render: (cogs, record) => (
                <div className="d-flex align-items-center px-2">
                    {cogs || "N/A"}
                    <Link
                        to=""
                        onClick={() => isSpApiConnected && handleEditCogs(record)}
                        style={{ marginLeft: "10px", color: isSpApiConnected ? "#333" : "#ccc" }}
                    >
                        <i className="ki-outline ki-plus-circle" />
                    </Link>
                </div>
            ),
            sorter: (a, b) => a.item_cogs - b.item_cogs
        },
        {
            title: "Replenishment Time (Days)",
            dataIndex: "replenishment_time",
            key: "replenishment_time",
            render: (value, record) => editableCell(record, "replenishment_time", value),
            sorter: (a, b) => a.replenishment_time - b.replenishment_time
        },
        {
            title: "FBM shipping cost",
            dataIndex: "fbm_shipping_cost_per_package",
            key: "fbm_shipping_cost_per_package",
            render: (value, record) => editableCell(record, "fbm_shipping_cost_per_package", value),
            sorter: (a, b) => a.fbm_shipping_cost_per_package - b.fbm_shipping_cost_per_package
        },
        {
            title: "Val. of unsellable return (%)",
            dataIndex: "unsellable_returns_percentage",
            key: "unsellable_returns_percentage",
            render: (value, record) => editableCell(record, "unsellable_returns_percentage", value),
            sorter: (a, b) => a.unsellable_returns_percentage - b.unsellable_returns_percentage
        },
        {
            title: "Tax Paid on COGS (%)",
            dataIndex: "product_tax_code",
            key: "product_tax_code",
            render: (value, record) => editableCell(record, "product_tax_code", value),
            sorter: (a, b) => a.product_tax_code - b.product_tax_code
        },
        {
            title: "Tax Collected on Sales (%)",
            dataIndex: "vat_rate",
            key: "vat_rate",
            render: (value, record) => editableCell(record, "vat_rate", value),
            sorter: (a, b) => a.vat_rate - b.vat_rate
        }
    ];

    const theme = {
        token: {
            colorPrimary: "#f45b68",
            colorBgContainer: "#ffffff",
        },
    };

    const handleUpload = (file) => {
        if(!isSpApiConnected) return false;
        message.success(`${file.name} file uploaded successfully.`);
        return false;
    };

    const handleSearch = (e) => {
        setSearchText(e.target.value);
        setPage(1);
    };

    const menu = (
        <Menu>
            <Menu.Item key="1" className="py-4" disabled>
                <div className="fs-5 text-gray-900 fw-bold">Import product data</div>
            </Menu.Item>
            <Menu.Divider />
            <div className="py-2 px-3">
                <div className="d-flex justify-content-between w-100">
                    <p className="mb-0">Download a template</p>
                    <a href="/assets/media/templates/Product_Import_Template.xlsx" download className="text-danger">Template <DownloadOutlined /></a>
                </div>
            </div>
            <Menu.Divider />
            <div className="py-2 px-3">
                <Upload beforeUpload={handleUpload} disabled={!isSpApiConnected}>
                    <Button disabled={!isSpApiConnected} className="w-100 fs-7 h-45px border border-dashed border-danger">
                        <i className="ki-duotone ki-file-up fs-2x text-danger"><span className="path1"/><span className="path2"/></i>
                        Drop files here or click to upload
                    </Button>
                </Upload>
            </div>
        </Menu>
    );

    const handleEditCogs = (record) => {
        setSelectedProduct(record);
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setSelectedProduct(null);
    };

    return (
        <Wrapper>
            <Helmet><title>Product</title></Helmet>
            <style>{`
            .editable-cell-container:hover { background-color: #f8f9fa; border: 1px dashed #f45b68; }
            input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
            input[type=number] { -moz-appearance: textfield; }
        `}`</style>

            {/* Banners */}
            {!isSpApiConnected && (
                <Alert
                    message={<span className="fw-bolder fs-5">Action Required: Connect Marketplace</span>}
                    description={
                        <div>
                            <p className="mb-2">Your Amazon Selling Partner API is not connected. This is required to manage and sync your business expenses.</p>
                            <Link to="/settings/marketplace-credentials?tab=spApi&creds=true">
                                <Button type="primary" danger size="small">Connect SP-API</Button>
                            </Link>
                        </div>
                    }
                    type="error"
                    showIcon
                    className="mb-4 p-6"
                />
            )}

            {/* 2. Ads API Banner (Warning Level) */}
            {!isAdsApiConnected && (
                <Alert
                    message={<span className="fw-bolder fs-6">Amazon Advertising Not Connected</span>}
                    description={
                        <div>
                            <p className="mb-2">PPC and Advertising-related expenses will not be accurately reflected in your metrics.</p>
                            {/* Redirecting to advertising tab and triggering the auto-modal logic we discussed */}
                            <Link to="/settings/marketplace-credentials?tab=advertising&creds=true">
                                <Button type="primary" danger size="small">Connect Ads API</Button>
                            </Link>
                        </div>
                    }
                    type="error"
                    showIcon
                    closable={isSpApiConnected} // Only allow closing if the main API is connected
                    className="mb-4 p-6"
                />
            )}

            <div style={{ position: 'relative' }}>

                {/* Overlay for SP-API disconnection */}
                {!(isSpApiConnected && isAdsApiConnected) && (
                    <div style={{
                        position: 'absolute',
                        top: 0, left: -10, right: -10, bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        backdropFilter: 'blur(5px)',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '12px'
                    }}>
                        <div className="text-center bg-white p-8 shadow-sm rounded border">
                            <i className="ki-outline ki-lock-2 fs-3x text-muted mb-4"></i>
                            <h3 className="text-gray-800">Inventory Locked</h3>
                            <p className="text-muted">Connect your Amazon account to manage product data.</p>
                        </div>
                    </div>
                )}

                <div className="mb-10 mx-n9 px-10 py-5 bg-white filter">
                    <div className="position-relative ">
                        <i className="ki-outline ki-magnifier text-gray-800 fs-2 position-absolute top-50 translate-middle-y ms-4" />
                        <input
                            type="text"
                            value={searchText}
                            onChange={handleSearch}
                            disabled={!isSpApiConnected}
                            className="form-control form-control-solid text-dark w-300px border border-gray-300 fs-7 ps-12 all-search"
                            placeholder="Search by sku, title or asin..."
                        />
                    </div>
                </div>

                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-header border-bottom-dashed">
                                <h3 className="card-title fw-bolder">Products List</h3>
                                <div className="card-toolbar">
                                    <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight" disabled={!isSpApiConnected}>
                                        <button className="btn btn-danger fs-7 fw-bold" disabled={!isSpApiConnected}>
                                            Import <i className="ms-1 bi bi-download fs-4" />
                                        </button>
                                    </Dropdown>
                                </div>
                            </div>
                            <div className="card-body py-2">
                                <div className="table-responsive">
                                    <Table
                                        scroll={{ x: "max-content" }}
                                        columns={columns}
                                        loading={loading}
                                        dataSource={list}
                                        rowKey="id"
                                        pagination={false}
                                        components={{
                                            table: (props) => <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...props} />,
                                            header: {
                                                wrapper: (props) => <thead {...props} />,
                                                row: (props) => <tr {...props} className="fw-bolder text-dark bg-cus" />,
                                                cell: (props) => <th {...props} />,
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-body py-2">
                                <ConfigProvider theme={theme}>
                                    <Pagination
                                        className="mt-3"
                                        current={page}
                                        pageSize={pageSize}
                                        total={totalPage}
                                        disabled={!isSpApiConnected}
                                        showSizeChanger
                                        onChange={(currentPage, newPageSize) => {
                                            setPage(currentPage);
                                            setPageSize(newPageSize);
                                        }}
                                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                                    />
                                </ConfigProvider>
                            </div>
                        </div>
                    </div>
                </div>

                <AddCogs
                    visible={isModalVisible}
                    onClose={handleCloseModal}
                    product={selectedProduct}
                    userData={userData}
                    getList={() => getProductList()}
                />
            </div>
        </Wrapper>
    );
};

export default Products;